import { computed, toValue, ref } from 'vue';
import { DEAD_ZONE, DEFAULT_THRESHOLD } from './constants';
import type { SwipeDirection, UseSwipeTriggerOptions } from './types';
import { clampToDirection } from './utils/clampToDirection';
import { getAxisDelta } from './utils/getAxisDelta';
import { getCssTouchAction } from './utils/getCssTouchAction';
import { isExceededThreshold } from './utils/isExceededThreshold';
import { isValidPointer } from './utils/isValidPointer';


/**
 * Returns pointer event bindings and reactive drag state for implementing
 * swipe-to-dismiss with a follow-finger drag effect.
 *
 * During a drag gesture, `dragOffset` tracks displacement along the trigger axis.
 * Positive values correspond to `right`/`down` directions; negative to `left`/`up`.
 * The value is clampedDelta so it never crosses zero in the opposite direction.
 *
 * On release past `threshold`, `onTrigger` is called with `dragOffset` retained
 * at its final position — the host element can continue its exit animation from there.
 * Call `reset()` to restore zero state, typically via `<Transition @after-leave>`.
 *
 * On release below `threshold`, `isDragging` becomes `false` and `dragOffset`
 * is reset to `0` after a `requestAnimationFrame` tick so a CSS
 * `transition: translate` on the element produces the snap-back animation.
 *
 * Spread `bindings` onto the swipe target element via `v-bind`.
 *
 * @example
 * ```vue
 * <Transition @after-leave="swipe.reset">
 *   <div
 *     v-if="isOpened"
 *     v-bind="swipe.bindings"
 *     :class="{ 'is-dragging': swipe.isDragging.value }"
 *     :style="{ translate: `${swipe.dragOffset.value}px 0` }"
 *   />
 * </Transition>
 * ```
 *
 * @param options - Swipe trigger configuration.
 * @returns Drag state refs, a `reset` method, and `bindings` to spread on the element.
 */
export const useSwipeTrigger = (options: UseSwipeTriggerOptions) => {
    const {
        direction,
        onTrigger,
        disabled = false,
        threshold = DEFAULT_THRESHOLD,
        pointerType,
    } = options;

    if (!direction || !onTrigger) {
        throw new Error('useSwipeTrigger: invalid initial options.');
    }

    // state

    let startX = 0;
    let startY = 0;
    // Direction is locked on pointerdown so that reactive changes mid-gesture
    // cannot corrupt clamp/threshold calculations.
    let activeDirection: SwipeDirection | null = null;

    // current drag displacement in pixels along the trigger axis
    const dragOffset = ref(0);
    // `true` while the user's pointer is actively dragging
    const isDragging = ref(false);


    //
    // api
    //

    const reset = (): void => {
        dragOffset.value = 0;
        isDragging.value = false;
        activeDirection = null;
    };

    const onPointerdown = (event: PointerEvent): void => {
        if (!isValidPointer(event, pointerType)) return;

        startX = event.clientX;
        startY = event.clientY;
        dragOffset.value = 0;
        activeDirection = toValue(direction);
    };

    const onPointermove = (event: PointerEvent): void => {
        if (!isValidPointer(event, pointerType)) return;
        if (!activeDirection) return;
        if (toValue(disabled)) return;

        const delta = getAxisDelta(event, startX, startY, activeDirection);

        const clampedDelta = clampToDirection(delta, activeDirection);

        if (!isDragging.value) {
            if (Math.abs(clampedDelta) < DEAD_ZONE) return;

            isDragging.value = true;
            (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        }

        dragOffset.value = clampedDelta;
    };

    const onPointerup = (event: PointerEvent): void => {
        if (!isValidPointer(event, pointerType)) return;
        if (!activeDirection) return;
        
        if (!isDragging.value) {
            reset();
            return;
        }

        const _direction = activeDirection;

        isDragging.value = false;

        // rAF ensures Vue flushes the isDragging=false DOM update (which re-enables
        // the CSS transition on `translate`) before we change dragOffset, so the
        // browser actually animates the snap-back rather than jumping instantly.
        requestAnimationFrame(() => {
            const delta = getAxisDelta(event, startX, startY, _direction);

            if (!toValue(disabled) && isExceededThreshold(delta, _direction, threshold)) {
                // dragOffset intentionally not reset here: the exit animation continues
                // from the dragged position. The caller resets state via reset() in @after-leave.
                //requestAnimationFrame(() => {
                onTrigger();
            }

            reset();
        });
    };

    const cssTouchAction = computed(() => getCssTouchAction(toValue(direction)));

    const preventHandler = (event: PointerEvent): void => {
        event.stopPropagation();
    }

    return {
        dragOffset,
        isDragging,
        cssTouchAction,
        reset,
        bindings: {
            onPointerdownPassive: onPointerdown,
            onPointermovePassive: onPointermove,
            onPointerupPassive: onPointerup,
            onPointercancelPassive: onPointerup,
        },
        preventBindings: {
            onPointerdown: preventHandler,
            onPointermove: preventHandler,
            onPointerup: preventHandler,
            onPointercancel: preventHandler,
        },
    };
};
